import dayjs from "dayjs";
import api from "../../api/api";
import { V2_BASE_URL } from "../../api/bases";
import { getPracticeId } from "../../config/practice";
import { tableDaySeconds } from "./reportHours";

/* Attendance by department (owner 11-03). An employee's department is the one on their user record
   (user_master.config.departmentid; all-departments users count under their home department). Timesheet
   rows carry emp_id = user_master.id. New v2 route, pinned like the recap / corrections calls; if it does
   not answer, the page shows no department filter and nothing else changes. */
const V2 = { baseURL: V2_BASE_URL, _gw: "v2-pinned" };

export const fetchAttendanceDepartments = () =>
  api.get(`/v1/${getPracticeId()}/timesheet/departments`, V2);

export const UNASSIGNED = "none";

/* {emp_id -> "1" | "2" | "3" | "none"} */
export const buildDepartmentMap = (employees) => {
  const map = {};
  (Array.isArray(employees) ? employees : []).forEach((e) => {
    if (!e || e.emp_id == null) return;
    map[String(e.emp_id)] = e.departmentid != null ? String(e.departmentid) : UNASSIGNED;
  });
  return map;
};

// all_periods rows: empid (user_master.id; emp_id is null on a row without a clock-in); recap rows: emp_id
export const rowEmployeeId = (row) => {
  const id = row?.empid ?? row?.emp_id;
  return id == null ? null : String(id);
};

export const departmentOf = (map, row) => {
  const id = rowEmployeeId(row);
  if (id == null) return UNASSIGNED;
  return (map && map[id]) || UNASSIGNED;
};

export const filterByDepartment = (rows, map, filter) => {
  if (!filter || !Array.isArray(rows)) return Array.isArray(rows) ? rows : [];
  return rows.filter((r) => departmentOf(map, r) === String(filter));
};

const emptyBucket = (key, name) => ({ key, name, employees: 0, clockedIn: 0, seconds: 0 });

const buckets = (departments) => {
  const out = {};
  (departments || []).forEach((d) => {
    out[String(d.departmentid)] = emptyBucket(String(d.departmentid), d.name);
  });
  out[UNASSIGNED] = emptyBucket(UNASSIGNED, "Not set");
  return out;
};

const bucketFor = (out, key) => {
  if (!out[key]) out[key] = emptyBucket(key, `Department ${key}`);
  return out[key];
};

const rowSeconds = (row) => {
  if (!row || !row.clock_in_time || !row.clock_out_time) return 0;
  if (String(row.status || "").toLowerCase() === "deleted") return 0;
  const diff = dayjs(row.clock_out_time).diff(dayjs(row.clock_in_time), "second");
  return Number.isFinite(diff) && diff > 0 ? diff : 0;
};

/* Daily view (all_periods rows: one per employee and Morning / Afternoon period). Per department:
   employees listed, employees clocked in, and recorded hours (clock-out minus clock-in, Deleted rows out). */
export const dailyTotals = (rows, map, departments) => {
  const out = buckets(departments);
  const seen = {};
  const clocked = {};
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    const key = departmentOf(map, r);
    const b = bucketFor(out, key);
    const emp = rowEmployeeId(r);
    if (emp != null && !seen[emp]) { seen[emp] = true; b.employees += 1; }
    if (emp != null && r.clock_in_time && !clocked[emp]) { clocked[emp] = true; b.clockedIn += 1; }
    b.seconds += rowSeconds(r);
  });
  return Object.values(out);
};

/* Reports view (recap rows: one per employee with recap[] days). Hours as the Reports table counts them
   (approved hours unless the Deleted filter is on: reportHours.tableDaySeconds). */
export const recapTotals = (rows, map, departments, approvedOnly) => {
  const out = buckets(departments);
  (Array.isArray(rows) ? rows : []).forEach((emp) => {
    const b = bucketFor(out, departmentOf(map, emp));
    b.employees += 1;
    let worked = false;
    (Array.isArray(emp.recap) ? emp.recap : []).forEach((day) => {
      const s = tableDaySeconds(day, approvedOnly);
      b.seconds += s;
      if (s > 0) worked = true;
    });
    if (worked) b.clockedIn += 1;
  });
  return Object.values(out);
};

export const formatHours = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
};
